// Relatórios da frota (E8): km rodados e consumo de cada caminhão, mês a mês.
// Tudo é calculado no aparelho, a partir das tabelas da empresa (funciona sem
// sinal). As fórmulas são as do modelo (docs/modelo-v2.md):
//
//   km do mês = maior hodômetro do mês − maior hodômetro até o fim do mês anterior
//   consumo   = km entre o 1º e o último abastecimento ÷ litros abastecidos depois do 1º
import { NOMES_CREDITO, NOMES_DESCONTO } from './demonstrativo.js';
import { abastecimentosEmOrdem } from './despesas.js';
import { data as dataBR, dinheiro, numeroBR, placa as formatarPlaca } from './formato.js';

const temNumero = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
const centavos = (v) => Math.round((Number(v) || 0) * 100);
const somaLitros = (lista) => Math.round(lista.reduce((s, a) => s + Number(a.litros || 0) * 1000, 0)) / 1000;

// ---------------------------------------------------------------------
// Meses e períodos ('AAAA-MM' e 'AAAA-MM-DD')
// ---------------------------------------------------------------------
export function ultimoDiaDoMes(mes) {
  const [a, m] = mes.split('-').map(Number);
  return new Date(Date.UTC(a, m, 0)).toISOString().slice(0, 10);
}

export const periodoDoMes = (mes) => ({ de: `${mes}-01`, ate: ultimoDiaDoMes(mes) });

// Os pedaços de um período em cada mês: [{ mes, de, ate, inteiro }] (o
// primeiro e o último podem não ser o mês inteiro, quando o período é entre
// datas).
export function trechosDoPeriodo(de, ate) {
  return mesesEntre(de, ate).map((mes) => {
    const p = periodoDoMes(mes);
    return { mes, de: p.de > de ? p.de : de, ate: p.ate < ate ? p.ate : ate, inteiro: p.de >= de && p.ate <= ate };
  });
}

// Os meses de um período, do primeiro ao último (no máximo 20 anos).
export function mesesEntre(de, ate) {
  const lista = [];
  let [a, m] = de.slice(0, 7).split('-').map(Number);
  const fim = ate.slice(0, 7);
  for (let i = 0; i < 240; i++) {
    const mes = `${a}-${String(m).padStart(2, '0')}`;
    if (mes > fim) break;
    lista.push(mes);
    m += 1;
    if (m > 12) {
      m = 1;
      a += 1;
    }
  }
  return lista;
}

// ---------------------------------------------------------------------
// Km rodados
// ---------------------------------------------------------------------
// Leituras de hodômetro de um caminhão: as dos lançamentos (abastecimento e
// qualquer despesa com km) e as da saída e da chegada das viagens.
export function leiturasDoVeiculo(veiculoId, { lancamentos = [], viagens = [] }) {
  const lista = [];
  for (const l of lancamentos) {
    if (!l.deleted_at && l.veiculo_id === veiculoId && temNumero(l.hodometro)) lista.push({ data: l.data, km: Number(l.hodometro) });
  }
  for (const v of viagens) {
    if (v.deleted_at || v.veiculo_id !== veiculoId) continue;
    if (temNumero(v.km_saida)) lista.push({ data: v.data_saida, km: Number(v.km_saida) });
    if (temNumero(v.km_chegada) && v.data_chegada) lista.push({ data: v.data_chegada, km: Number(v.km_chegada) });
  }
  return lista.sort((a, b) => (a.data < b.data ? -1 : a.data > b.data ? 1 : a.km - b.km));
}

// Km rodados no período: maior leitura do período − maior leitura antes dele.
// Sem leitura antes (começo do histórico), conta da primeira leitura do
// período (`desdeAPrimeira`). Sem leitura no período, km fica null: esses km
// entram no período da leitura seguinte. `desde`: o dia da leitura de onde a
// conta parte; `acumulado`: ela é de antes do mês anterior ao período (os km
// juntam meses sem leitura e não servem para o custo por km do período).
export function kmNoPeriodo(leituras, { de, ate }) {
  const doPeriodo = leituras.filter((l) => l.data >= de && l.data <= ate);
  if (doPeriodo.length === 0) return { km: null, semLeitura: true, desdeAPrimeira: false, acumulado: false };
  const antes = leituras.filter((l) => l.data < de);
  const maiorDe = (lista) => lista.reduce((m, l) => (l.km > m.km || (l.km === m.km && l.data > m.data) ? l : m));
  const final = maiorDe(doPeriodo);
  const base = antes.length > 0 ? maiorDe(antes) : doPeriodo.reduce((m, l) => (l.km < m.km ? l : m));
  const mesAnterior = mesesAntes(de, 1);
  return {
    km: Math.max(0, final.km - base.km),
    inicial: base.km,
    final: final.km,
    desde: base.data,
    semLeitura: false,
    desdeAPrimeira: antes.length === 0,
    acumulado: antes.length > 0 && base.data < mesAnterior,
  };
}

// Km só de dentro do período: da primeira à maior leitura dele. Serve quando
// a última leitura antes do período é antiga (os km juntariam meses de fora).
export function kmDentroDoPeriodo(leituras, { de, ate }) {
  const doPeriodo = leituras.filter((l) => l.data >= de && l.data <= ate);
  if (doPeriodo.length === 0) return { km: null, semLeitura: true };
  const primeira = doPeriodo.reduce((m, l) => (l.km < m.km ? l : m));
  const maior = Math.max(...doPeriodo.map((l) => l.km));
  return { km: Math.max(0, maior - primeira.km), inicial: primeira.km, final: maior, desde: primeira.data, semLeitura: false, dentroDoPeriodo: true };
}

// Primeiro dia do mês `n` meses antes do dia.
function mesesAntes(dia, n) {
  const [a, m] = dia.split('-').map(Number);
  const d = new Date(Date.UTC(a, m - 1 - n, 1));
  return d.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------
// Consumo
// ---------------------------------------------------------------------
// Abastecimentos de um caminhão: despesas das categorias de combustível
// (`idsDiesel`, as que exigem hodômetro) com litros.
export function abastecimentosDoVeiculo(veiculoId, lancamentos, idsDiesel) {
  return lancamentos.filter((l) => !l.deleted_at && l.tipo === 'despesa' && l.veiculo_id === veiculoId
    && idsDiesel.has(l.categoria_id) && Number(l.litros) > 0);
}

// Consumo no período, de tanque cheio a tanque cheio: do último abastecimento
// com km antes do período (ou do primeiro com km dentro dele) até o último
// com km dentro dele, com os litros abastecidos depois do ponto de partida
// (os sem km no meio entram). Assim os meses se somam: o trecho entre um mês
// e o outro fica no mês em que termina.
export function consumoNoPeriodo(abastecimentos, { de, ate }) {
  const lista = abastecimentosEmOrdem(abastecimentos);
  const comKm = (a) => temNumero(a.hodometro);
  const fim = lista.findLastIndex((a) => a.data >= de && a.data <= ate && comKm(a));
  if (fim < 0) return null;
  let inicio = -1;
  for (let i = fim - 1; i >= 0; i--) {
    if (lista[i].data < de && comKm(lista[i])) {
      inicio = i;
      break;
    }
  }
  if (inicio < 0) inicio = lista.findIndex((a) => a.data >= de && comKm(a));
  if (inicio < 0 || inicio >= fim) return null;
  const km = Number(lista[fim].hodometro) - Number(lista[inicio].hodometro);
  const litros = somaLitros(lista.slice(inicio + 1, fim + 1));
  if (!(km > 0) || !(litros > 0)) return null;
  return { km, litros, kmPorLitro: km / litros, desde: lista[inicio].data, ate: lista[fim].data };
}

// ---------------------------------------------------------------------
// Km e consumo de um caminhão, mês a mês
// ---------------------------------------------------------------------
// `dados`: { lancamentos, viagens } da empresa · `idsDiesel`: Set das
// categorias de combustível · `meses`: ['AAAA-MM', …]
export function kmEConsumoPorMes(veiculoId, dados, idsDiesel, meses) {
  const leituras = leiturasDoVeiculo(veiculoId, dados);
  const abastecimentos = abastecimentosDoVeiculo(veiculoId, dados.lancamentos ?? [], idsDiesel);
  return meses.map((mes) => {
    const p = periodoDoMes(mes);
    const doMes = abastecimentos.filter((a) => a.data >= p.de && a.data <= p.ate);
    return {
      mes,
      ...kmNoPeriodo(leituras, p),
      consumo: consumoNoPeriodo(abastecimentos, p),
      abastecimentos: doMes.length,
      litros: somaLitros(doMes),
      valorDiesel: doMes.reduce((s, a) => s + centavos(a.valor), 0) / 100,
    };
  });
}

// ---------------------------------------------------------------------
// Custo por km e resultado de cada caminhão
// ---------------------------------------------------------------------
//   custos    = despesas do caminhão e da carreta acoplada (as categorias que
//               entram no custo/km; as contas fixas já viram despesa do mês)
//             + parte das despesas gerais (sem caminhão), pelo rateio escolhido
//             + remuneração do motorista (lançada nos acertos e no histórico,
//               e a prevista pelas regras para o que ainda não foi acertado,
//               inclusive o fixo pago pela folha)
//             + agenciamento das cargas (à agenciadora ou à transportadora;
//               decidido com o titular em 05/10/2026)
//   custo/km  = custos ÷ km
//   sobrou    = fretes + estadias − descontos do contratante − custos
//
// Cada mês é calculado separado (o rateio usa os km do mês) e o período soma
// os meses (entre datas, o primeiro e o último podem ser só um pedaço do mês).
// Competência: a data da carga e a data do crédito. As despesas (pedido do
// titular em 13/10/2026): pela data da compra (a compra a prazo inteira, no
// dia dela) ou pelo que foi pago (à vista na data dela; a prazo, cada parcela
// no dia em que foi paga; as que faltam pagar aparecem à parte).
export const DESPESAS = {
  pagamento: 'pelo que foi pago (à vista na data dela; a prazo, só as parcelas pagas no período)',
  lancamento: 'pela data da compra (a compra a prazo inteira, no dia dela)',
};

export const RATEIOS = {
  km: 'pelos km rodados',
  igual: 'meio a meio entre os caminhões com placa ativa',
  nenhum: 'não entram no custo dos caminhões',
};

const reais = (c) => c / 100;
const vivo = (x) => x && !x.deleted_at;

function diaAnterior(iso) {
  const [a, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(a, m - 1, d - 1)).toISOString().slice(0, 10);
}

function diasEntreInclusive(de, ate) {
  const [ya, ma, da] = de.split('-').map(Number);
  const [yb, mb, db] = ate.split('-').map(Number);
  return Math.round((Date.UTC(yb, mb - 1, db) - Date.UTC(ya, ma - 1, da)) / 86400000) + 1;
}

// Divide `total` (centavos) pelos pesos, sem perder centavo: a sobra vai para
// o maior peso. `pesos`: Map(chave → peso > 0).
export function ratear(total, pesos) {
  const soma = [...pesos.values()].reduce((s, p) => s + p, 0);
  const partes = new Map();
  if (!(soma > 0) || total === 0) return partes;
  let usado = 0;
  let maior = null;
  for (const [k, p] of pesos) {
    const parte = Math.floor((total * p) / soma);
    partes.set(k, parte);
    usado += parte;
    if (maior === null || p > pesos.get(maior)) maior = k;
  }
  partes.set(maior, partes.get(maior) + (total - usado));
  return partes;
}

// Tudo o que o relatório precisa, indexado uma vez.
function prepararDados({
  veiculos = [], licencas = [], viagens = [], fretes = [], lancamentos = [], categorias = [], regras = [], parcelas = [], clientes = [], colaboradores = [],
}) {
  const tratores = veiculos.filter((v) => vivo(v) && v.tipo === 'trator');
  // A carreta na lixeira continua levando as despesas dela para o trator.
  const tratorDaCarreta = new Map(veiculos.filter((v) => v.tipo === 'implemento' && v.acoplado_a).map((v) => [v.id, v.acoplado_a]));
  const viagensVivas = viagens.filter((v) => vivo(v) && v.status !== 'cancelada');
  const viagemPorId = new Map(viagensVivas.map((v) => [v.id, v]));
  const fretesVivos = fretes.filter((f) => vivo(f) && viagemPorId.has(f.viagem_id));
  const fretePorId = new Map(fretesVivos.map((f) => [f.id, f]));
  const categoriaPorId = new Map(categorias.map((c) => [c.id, c]));
  const lancs = lancamentos.filter(vivo);
  const parcelasDe = new Map();
  for (const pa of parcelas.filter(vivo)) {
    if (!parcelasDe.has(pa.lancamento_id)) parcelasDe.set(pa.lancamento_id, []);
    parcelasDe.get(pa.lancamento_id).push(pa);
  }
  return {
    tratores,
    tratorDaCarreta,
    licencas: licencas.filter(vivo),
    viagens: viagensVivas,
    viagemPorId,
    fretes: fretesVivos,
    fretePorId,
    categoriaPorId,
    idsDiesel: new Set(categorias.filter((c) => c.exige_hodometro).map((c) => c.id)),
    lancamentos: lancs,
    creditos: lancs.filter((l) => l.tipo === 'credito'),
    regras: regras.filter(vivo),
    parcelasDe,
    veiculoPorId: new Map(veiculos.map((v) => [v.id, v])),
    clientePorId: new Map(clientes.map((c) => [c.id, c])),
    pessoaPorId: new Map(colaboradores.map((c) => [c.id, c])),
  };
}

// ---------------------------------------------------------------------
// Os lançamentos de cada linha (pedido do titular em 13/10/2026: "tem que
// aparecer cada valor discriminado, até para que eu possa conferir").
//   { linha, data, nome, detalhe, c (centavos) }
//   linha: diesel | viagem | manutencao | fixo | outros | gerais | motorista
//          | agenciamento | frete | estadia | desconto | imposto
// ---------------------------------------------------------------------
const juntar = (...partes) => partes.filter(Boolean).join(' · ');
const lugar = (cidade, uf) => [cidade, uf].filter(Boolean).join('/');
function rotaDaCarga(f) {
  const de = lugar(f.origem_cidade, f.origem_uf);
  const para = lugar(f.destino_cidade, f.destino_uf);
  return de || para ? `${de || '?'} → ${para || '?'}` : '';
}
const nomeDaPessoa = (d, id) => {
  const p = d.pessoaPorId.get(id);
  return p ? p.apelido || p.nome : null;
};

// A linha de uma despesa no relatório (a do caminhão, pelo grupo da categoria).
function linhaDaDespesa(d, l, cat) {
  if (d.idsDiesel.has(l.categoria_id)) return 'diesel';
  const grupo = cat?.grupo ?? 'outros';
  return ['viagem', 'manutencao', 'fixo'].includes(grupo) ? grupo : 'outros';
}

function itemDaDespesa(d, { l, c, data, parcela }, linha, caminhao = null) {
  const cat = d.categoriaPorId.get(l.categoria_id);
  const carreta = caminhao && l.veiculo_id && l.veiculo_id !== caminhao ? d.veiculoPorId.get(l.veiculo_id) : null;
  return {
    linha,
    data,
    nome: cat?.nome ?? 'Despesa',
    detalhe: juntar(
      l.fornecedor, l.descricao,
      l.documento_numero ? `nota ${l.documento_numero}` : null,
      Number(l.litros) > 0 ? `${numeroBR(Number(l.litros))} litros` : null,
      carreta ? `carreta ${formatarPlaca(carreta.placa ?? '')}` : null,
      parcela ? `parcela ${parcela}` : l.condicao === 'a_prazo' ? 'compra a prazo, inteira' : null,
      l.pago_por === 'motorista' ? 'paga pelo motorista' : null,
    ),
    c,
  };
}

// Divide `total` entre os itens, na proporção do valor de cada um (a sobra
// dos centavos vai no último): a parte de um caminhão nas despesas gerais.
function parteDosItens(itens, total) {
  const soma = itens.reduce((s, i) => s + i.c, 0);
  if (soma === 0 || total === 0) return [];
  let usado = 0;
  return itens.map((i, n) => {
    const parte = n === itens.length - 1 ? total - usado : Math.round((i.c * total) / soma);
    usado += parte;
    return { ...i, linha: 'gerais', c: parte, detalhe: juntar(i.detalhe, `parte de ${dinheiro(i.c / 100)}`) };
  }).filter((i) => i.c !== 0);
}

// As parcelas de uma compra a prazo (sem elas, a compra conta inteira).
const parcelasDaCompra = (l, d) => (l.condicao === 'a_prazo' ? d.parcelasDe.get(l.id) ?? [] : []);

// As despesas de um trecho, em centavos: [{ l, c, data, parcela }]. Pela data
// da compra, ou pelo que foi pago nele (a prazo, as parcelas pagas no trecho,
// na data do pagamento).
function despesasDoTrecho(d, p, modo) {
  const lista = [];
  for (const l of d.lancamentos) {
    if (l.tipo !== 'despesa') continue;
    const parcelas = modo === 'pagamento' ? parcelasDaCompra(l, d) : [];
    if (parcelas.length > 0) {
      for (const pa of parcelas) {
        if (pa.pago_em && pa.pago_em >= p.de && pa.pago_em <= p.ate) {
          lista.push({ l, c: centavos(pa.valor), data: pa.pago_em, parcela: `${pa.numero}/${pa.total}` });
        }
      }
    } else if (l.data >= p.de && l.data <= p.ate) {
      lista.push({ l, c: centavos(l.valor), data: l.data, parcela: null });
    }
  }
  return lista;
}

// O que ficou para pagar no fim do período: as parcelas das compras a prazo
// feitas até ele e não pagas até ele. Por caminhão (Map) e sem caminhão, em
// centavos e quantas parcelas.
function emAbertoNoFim(d, ate) {
  const porCaminhao = new Map();
  const semCaminhao = { c: 0, parcelas: 0, itens: [] };
  for (const l of d.lancamentos) {
    if (l.tipo !== 'despesa' || l.data > ate) continue;
    for (const pa of parcelasDaCompra(l, d)) {
      if (pa.pago_em && pa.pago_em <= ate) continue;
      const caminhao = caminhaoDaDespesa(l, d);
      const alvo = caminhao ? porCaminhao.get(caminhao) ?? { c: 0, parcelas: 0, itens: [] } : semCaminhao;
      const c = centavos(pa.valor);
      alvo.c += c;
      alvo.parcelas += 1;
      // A parcela em aberto, com o vencimento
      alvo.itens.push(itemDaDespesa(d, { l, c, data: pa.vencimento, parcela: `${pa.numero}/${pa.total}, vence nesse dia` }, 'aberto', caminhao));
      if (caminhao) porCaminhao.set(caminhao, alvo);
    }
  }
  return { porCaminhao, semCaminhao };
}

// O caminhão de uma despesa: o dela, o trator da carreta, ou o da viagem.
function caminhaoDaDespesa(l, d) {
  if (l.veiculo_id) return d.tratorDaCarreta.get(l.veiculo_id) ?? l.veiculo_id;
  if (l.viagem_id) return d.viagemPorId.get(l.viagem_id)?.veiculo_id ?? null;
  return null;
}

// O caminhão de um crédito ou desconto: o da viagem, ou o da viagem da carga.
function caminhaoDoCredito(l, d) {
  if (l.viagem_id && d.viagemPorId.has(l.viagem_id)) return d.viagemPorId.get(l.viagem_id).veiculo_id;
  if (l.frete_id && d.fretePorId.has(l.frete_id)) return d.viagemPorId.get(d.fretePorId.get(l.frete_id).viagem_id)?.veiculo_id ?? null;
  return null;
}

// Dias de viagem de um colaborador em cada caminhão, dentro do período (a
// viagem em andamento conta até `hoje`).
function diasPorCaminhao(d, colaboradorId, { de, ate }, hoje) {
  const dias = new Map();
  for (const v of d.viagens) {
    if (v.motorista_id !== colaboradorId) continue;
    const ini = v.data_saida > de ? v.data_saida : de;
    const fimViagem = v.data_chegada ?? hoje;
    const fim = fimViagem < ate ? fimViagem : ate;
    if (fim < ini) continue;
    dias.set(v.veiculo_id, (dias.get(v.veiculo_id) ?? 0) + diasEntreInclusive(ini, fim));
  }
  return dias;
}

function placaAtivaNoPeriodo(d, veiculoId, { de, ate }) {
  return d.licencas.some((l) => l.veiculo_id === veiculoId && l.inicio <= ate && (!l.fim || l.fim >= de));
}

// Remuneração do mês, em centavos, por caminhão (Map) e a que não tem
// caminhão (vai para as despesas gerais). Lançada: créditos e descontos dos
// acertos e do histórico (o agenciamento sai das cargas; o saldo de
// implantação não é custo do mês). Prevista: o que as regras dão para as
// cargas e viagens ainda não acertadas, o fixo pela folha e o fixo do acerto
// ainda não lançado.
function remuneracaoDoMes(d, p, hoje, regraValendo, creditosDaViagem) {
  const lancada = new Map();
  const prevista = new Map();
  let semCaminhao = { lancada: 0, prevista: 0 };
  const itens = new Map();
  const itensSemCaminhao = [];
  const somar = (mapa, k, c) => mapa.set(k, (mapa.get(k) ?? 0) + c);
  const anotar = (k, item) => {
    if (!itens.has(k)) itens.set(k, []);
    itens.get(k).push({ linha: 'motorista', ...item });
  };

  // Do colaborador (sem viagem): vai para os caminhões em que ele rodou no
  // mês, pelos dias de viagem; sem viagem no mês, fica sem caminhão.
  const doColaborador = (colaboradorId, c, tipo, item) => {
    const partes = ratear(c, diasPorCaminhao(d, colaboradorId, p, hoje));
    if (partes.size === 0) {
      semCaminhao = { ...semCaminhao, [tipo]: semCaminhao[tipo] + c };
      itensSemCaminhao.push({ ...item, c });
    }
    for (const [k, parte] of partes) {
      somar(tipo === 'lancada' ? lancada : prevista, k, parte);
      anotar(k, { ...item, c: parte, detalhe: partes.size > 1 || parte !== c ? juntar(item.detalhe, `parte pelos dias de viagem (de ${dinheiro(c / 100)})`) : item.detalhe });
    }
  };

  for (const l of d.lancamentos) {
    if ((l.tipo !== 'credito' && l.tipo !== 'desconto') || l.data < p.de || l.data > p.ate) continue;
    if (l.origem === 'implantacao' || l.subtipo === 'agenciamento') continue;
    const c = (l.tipo === 'desconto' ? -1 : 1) * centavos(l.valor);
    const caminhao = caminhaoDoCredito(l, d);
    const frete = l.frete_id ? d.fretePorId.get(l.frete_id) : null;
    const item = {
      data: l.data,
      nome: l.tipo === 'desconto' ? `Desconto: ${(NOMES_DESCONTO[l.subtipo] ?? 'outro').toLowerCase()}` : NOMES_CREDITO[l.subtipo] ?? 'Crédito',
      detalhe: juntar(nomeDaPessoa(d, l.colaborador_id), l.descricao, frete ? `carga de ${rotaDaCarga(frete) || dataBR(frete.data_carregamento)}` : null, l.acerto_id ? 'no acerto' : l.importacao_id ? 'histórico' : null),
    };
    if (caminhao) {
      somar(lancada, caminhao, c);
      anotar(caminhao, { ...item, c });
    } else if (l.colaborador_id) {
      doColaborador(l.colaborador_id, c, 'lancada', item);
    } else {
      semCaminhao.lancada += c;
      itensSemCaminhao.push({ ...item, c });
    }
  }

  // Previsto: viagens do HSA Frota ainda não acertadas por inteiro. As
  // diárias de uma viagem que vem do mês anterior contam só os dias deste mês
  // (como se as de antes já estivessem pagas).
  const ate = p.ate < hoje ? p.ate : hoje;
  const vespera = diaAnterior(p.de);
  for (const v of d.viagens) {
    if (v.importacao_id || v.status === 'acertada' || v.data_saida > ate) continue;
    const cargas = d.fretes.filter((f) => f.viagem_id === v.id && !f.acerto_id && f.data_carregamento <= ate);
    const jaLancados = v.data_saida < p.de
      ? [...d.creditos, { viagem_id: v.id, subtipo: 'diaria', data: vespera }]
      : d.creditos;
    for (const linha of creditosDaViagem(v, cargas, d.regras, { jaLancados, ate })) {
      if (linha.data < p.de || linha.data > p.ate) continue;
      somar(prevista, v.veiculo_id, centavos(linha.valor));
      anotar(v.veiculo_id, {
        data: linha.data,
        nome: NOMES_CREDITO[linha.subtipo] ?? 'Crédito',
        detalhe: juntar(nomeDaPessoa(d, v.motorista_id), linha.descricao, 'pelas regras, ainda não acertado'),
        c: centavos(linha.valor),
      });
    }
  }

  // Fixo mensal: pela folha (nunca passa no acerto) ou no acerto, ainda não
  // lançado. Num pedaço do mês (período entre datas), a parte dos dias dele.
  const mes = p.de.slice(0, 7);
  const parteDoMes = diasEntreInclusive(p.de, p.ate) / diasEntreInclusive(`${mes}-01`, ultimoDiaDoMes(mes));
  const diaDaRegra = p.ate < hoje ? p.ate : hoje;
  if (diaDaRegra >= p.de) {
    const comFixo = new Set(d.regras.filter((r) => r.componente === 'fixo_mensal' && r.colaborador_id).map((r) => r.colaborador_id));
    for (const colaboradorId of comFixo) {
      const regra = regraValendo(d.regras, 'fixo_mensal', { colaboradorId, dia: diaDaRegra });
      if (!regra || !(Number(regra.valor) > 0)) continue;
      const lancado = d.creditos.some((l) => l.colaborador_id === colaboradorId && l.subtipo === 'fixo_mensal' && l.data.slice(0, 7) === mes);
      if (regra.fixo_pagamento === 'folha' || !lancado) {
        doColaborador(colaboradorId, Math.round(centavos(regra.valor) * parteDoMes), 'prevista', {
          data: p.de,
          nome: NOMES_CREDITO.fixo_mensal,
          detalhe: juntar(
            nomeDaPessoa(d, colaboradorId),
            regra.fixo_pagamento === 'folha' ? 'pela folha' : 'pelas regras, ainda não acertado',
            parteDoMes < 1 ? `${diasEntreInclusive(p.de, p.ate)} dias de ${dinheiro(Number(regra.valor))} por mês` : null,
          ),
        });
      }
    }
  }
  return { lancada, prevista, semCaminhao, itens, itensSemCaminhao };
}

const vazioDoCaminhao = () => ({
  despesas: 0, porGrupo: {}, diesel: 0, gerais: 0, remuneracaoLancada: 0, remuneracaoPrevista: 0,
  cargas: 0, fretes: 0, estadias: 0, descontos: 0, agenciamentos: 0, itens: [],
});

// Um mês (ou o pedaço dele no período): os valores (em centavos) de cada
// caminhão e o que ficou sem caminhão.
function custosDoTrecho(d, { mes, de, ate, inteiro }, { hoje, rateio, despesas, regraValendo, creditosDaViagem }) {
  const p = { de, ate };
  const porCaminhao = new Map(d.tratores.map((t) => [t.id, vazioDoCaminhao()]));
  const doCaminhao = (id) => {
    if (!porCaminhao.has(id)) porCaminhao.set(id, vazioDoCaminhao());
    return porCaminhao.get(id);
  };
  let gerais = 0;
  let impostos = 0;
  const itensGerais = [];
  const itensImpostos = [];

  for (const x of despesasDoTrecho(d, p, despesas)) {
    const { l, c } = x;
    const cat = d.categoriaPorId.get(l.categoria_id);
    if (cat && cat.entra_custo_km === false) {
      impostos += c;
      itensImpostos.push(itemDaDespesa(d, x, 'imposto'));
      continue;
    }
    const caminhao = caminhaoDaDespesa(l, d);
    if (!caminhao) {
      gerais += c;
      itensGerais.push(itemDaDespesa(d, x, 'gerais'));
      continue;
    }
    const t = doCaminhao(caminhao);
    const grupo = cat?.grupo ?? 'outros';
    t.despesas += c;
    t.porGrupo[grupo] = (t.porGrupo[grupo] ?? 0) + c;
    if (d.idsDiesel.has(l.categoria_id)) t.diesel += c;
    t.itens.push(itemDaDespesa(d, x, linhaDaDespesa(d, l, cat), caminhao));
  }

  for (const f of d.fretes) {
    if (f.data_carregamento < p.de || f.data_carregamento > p.ate) continue;
    const t = doCaminhao(d.viagemPorId.get(f.viagem_id).veiculo_id);
    const daCarga = juntar(d.clientePorId.get(f.cliente_id)?.nome, rotaDaCarga(f), f.cte_numero ? `CT-e ${f.cte_numero}` : null);
    const item = (linha, nome, c, detalhe = daCarga) => c !== 0 && t.itens.push({ linha, data: f.data_carregamento, nome, detalhe, c });
    const descontos = centavos(f.desc_inss) + centavos(f.desc_sest_senat) + centavos(f.desc_irrf) + centavos(f.desc_outros);
    t.cargas += 1;
    t.fretes += centavos(f.valor_frete);
    t.estadias += centavos(f.estadia_valor);
    t.descontos += descontos;
    item('frete', f.complemento_de ? 'Complemento' : 'Frete', centavos(f.valor_frete));
    item('estadia', 'Estadia', centavos(f.estadia_valor));
    item('desconto', 'Descontos do contratante', descontos, juntar(
      daCarga,
      centavos(f.desc_inss) ? `INSS ${dinheiro(Number(f.desc_inss))}` : null,
      centavos(f.desc_sest_senat) ? `SEST/SENAT ${dinheiro(Number(f.desc_sest_senat))}` : null,
      centavos(f.desc_irrf) ? `IRRF ${dinheiro(Number(f.desc_irrf))}` : null,
      centavos(f.desc_outros) ? `outros ${dinheiro(Number(f.desc_outros))}` : null,
    ));
    if (f.agenciado_por === 'colaborador' || f.agenciado_por === 'transportadora') {
      t.agenciamentos += centavos(f.agenciamento_valor);
      item('agenciamento', 'Agenciamento', centavos(f.agenciamento_valor), juntar(
        f.agenciado_por === 'colaborador' ? nomeDaPessoa(d, f.agenciador_id) : 'transportadora', daCarga,
      ));
    }
  }

  const rem = remuneracaoDoMes(d, p, hoje, regraValendo, creditosDaViagem);
  for (const [id, c] of rem.lancada) doCaminhao(id).remuneracaoLancada += c;
  for (const [id, c] of rem.prevista) doCaminhao(id).remuneracaoPrevista += c;
  for (const [id, lista] of rem.itens) doCaminhao(id).itens.push(...lista);
  gerais += rem.semCaminhao.lancada + rem.semCaminhao.prevista;
  itensGerais.push(...rem.itensSemCaminhao.map((i) => ({ ...i, linha: 'gerais' })));

  // Km do mês de cada caminhão (para o rateio e o custo/km)
  const km = new Map();
  for (const id of porCaminhao.keys()) km.set(id, kmNoPeriodo(leiturasDoVeiculo(id, d), p));

  // Rateio das despesas gerais: pelos km do mês, ou meio a meio entre os
  // caminhões ativos no mês (placa ativa, ou algum km, despesa ou carga; o
  // histórico importado não tem placa). Sem km no mês, vai meio a meio.
  let pesos = new Map();
  if (rateio === 'km') {
    for (const [id, k] of km) if (k.km > 0) pesos.set(id, k.km);
  }
  if (rateio === 'igual' || (rateio === 'km' && pesos.size === 0)) {
    const ativo = (id) => {
      const t = porCaminhao.get(id);
      return placaAtivaNoPeriodo(d, id, p) || km.get(id)?.km > 0 || t.despesas !== 0 || t.cargas > 0;
    };
    pesos = new Map(d.tratores.map((t) => t.id).filter(ativo).map((id) => [id, 1]));
  }
  const partes = rateio === 'nenhum' ? new Map() : ratear(gerais, pesos);
  for (const [id, c] of partes) {
    doCaminhao(id).gerais += c;
    doCaminhao(id).itens.push(...parteDosItens(itensGerais, c));
  }
  const semRateio = gerais - [...partes.values()].reduce((s, c) => s + c, 0);

  return {
    mes, de, ate, inteiro, porCaminhao, km, gerais, semRateio, impostos,
    // O que não entrou em caminhão nenhum (sem rateio) e os impostos, um a um
    itensFora: semRateio !== 0 ? itensGerais : [],
    itensImpostos,
  };
}

// Totais de um caminhão (centavos → reais) e as contas. `km`: os km que
// servem para o custo por km (null: não dá para calcular).
function fecharCaminhao(t, km) {
  const remuneracao = t.remuneracaoLancada + t.remuneracaoPrevista;
  const custo = t.despesas + t.gerais + remuneracao + t.agenciamentos;
  const kmRodados = km ?? null;
  return {
    despesas: reais(t.despesas),
    porGrupo: Object.fromEntries(Object.entries(t.porGrupo).map(([g, c]) => [g, reais(c)])),
    diesel: reais(t.diesel),
    gerais: reais(t.gerais),
    remuneracao: { lancada: reais(t.remuneracaoLancada), prevista: reais(t.remuneracaoPrevista), total: reais(remuneracao) },
    custo: reais(custo),
    custoPorKm: kmRodados > 0 ? custo / 100 / kmRodados : null,
    cargas: t.cargas,
    fretes: reais(t.fretes),
    estadias: reais(t.estadias),
    descontos: reais(t.descontos),
    agenciamentos: reais(t.agenciamentos),
    sobrou: reais(t.fretes + t.estadias - t.descontos - custo),
    itens: itensEmReais(t.itens),
  };
}

// Os itens por data, em reais.
const itensEmReais = (itens) => [...itens]
  .sort((a, b) => String(a.data).localeCompare(String(b.data)) || a.nome.localeCompare(b.nome, 'pt-BR'))
  .map(({ c, ...i }) => ({ ...i, valor: reais(c) }));

function somarCaminhao(a, b) {
  const r = { ...a, porGrupo: { ...a.porGrupo }, itens: [...a.itens, ...b.itens] };
  for (const k of Object.keys(b)) {
    if (k === 'porGrupo') {
      for (const [g, c] of Object.entries(b.porGrupo)) r.porGrupo[g] = (r.porGrupo[g] ?? 0) + c;
    } else if (k !== 'itens') r[k] = (r[k] ?? 0) + b[k];
  }
  return r;
}

// O relatório dos caminhões num período (de/até em dias). `regraValendo` e
// `creditosDaViagem` vêm de lib/acerto.js (passados por quem chama, para este
// arquivo não depender do acerto).
//   rateio:    'km' | 'igual' | 'nenhum'
//   despesas:  'lancamento' (pela data da compra) | 'pagamento' (pelo que foi pago)
//   veiculoId: só esse caminhão (o rateio continua com a frota toda)
export function relatorioDosCaminhoes(tabelas, {
  de, ate, hoje, rateio = 'km', despesas = 'lancamento', veiculoId = null, regraValendo, creditosDaViagem,
}) {
  const d = prepararDados(tabelas);
  const meses = trechosDoPeriodo(de, ate).map((t) => custosDoTrecho(d, t, { hoje, rateio, despesas, regraValendo, creditosDaViagem }));
  const periodo = { de, ate };
  const aberto = despesas === 'pagamento' ? emAbertoNoFim(d, ate) : null;
  const doAberto = (x) => (x ? { valor: reais(x.c), parcelas: x.parcelas, itens: itensEmReais(x.itens) } : { valor: 0, parcelas: 0, itens: [] });
  const ids = new Set(meses.flatMap((m) => [...m.porCaminhao.keys()]));
  const veiculoPorId = new Map((tabelas.veiculos ?? []).map((v) => [v.id, v]));
  const caminhoes = [...ids].map((id) => {
    const total = meses.reduce((acc, m) => somarCaminhao(acc, m.porCaminhao.get(id) ?? vazioDoCaminhao()), vazioDoCaminhao());
    const leituras = leiturasDoVeiculo(id, d);
    const abastecimentos = abastecimentosDoVeiculo(id, d.lancamentos, d.idsDiesel);
    const doPeriodo = abastecimentos.filter((a) => a.data >= periodo.de && a.data <= periodo.ate);
    // Km do período pela fórmula; se a última leitura antes dele é antiga, só
    // os km de dentro do período (os de antes não são dele).
    const kmDoPeriodo = kmNoPeriodo(leituras, periodo);
    const km = kmDoPeriodo.acumulado ? kmDentroDoPeriodo(leituras, periodo) : kmDoPeriodo;
    return {
      veiculo: veiculoPorId.get(id) ?? { id },
      km: { ...km, ultimaAntes: kmDoPeriodo.acumulado ? kmDoPeriodo.desde : null },
      consumo: consumoNoPeriodo(abastecimentos, periodo),
      litros: somaLitros(doPeriodo),
      ...fecharCaminhao(total, km.km),
      emAberto: aberto ? doAberto(aberto.porCaminhao.get(id)) : null,
      // No mês, o custo por km fica sem conta quando os km não são só dele:
      // juntam meses sem leitura (a leitura de antes é de antes do mês
      // anterior) ou não cobrem o mês inteiro (o primeiro mês com leitura).
      meses: meses.map((m) => {
        const t = m.porCaminhao.get(id) ?? vazioDoCaminhao();
        const p = { de: m.de, ate: m.ate };
        const kmMes = m.km.get(id) ?? kmNoPeriodo(leituras, p);
        const incompleto = kmMes.acumulado || kmMes.desdeAPrimeira;
        const { itens, ...doMes } = fecharCaminhao(t, incompleto ? null : kmMes.km);
        return { mes: m.mes, de: m.de, ate: m.ate, inteiro: m.inteiro, km: kmMes, consumo: consumoNoPeriodo(abastecimentos, p), ...doMes };
      }),
    };
  });
  // Só os caminhões com alguma coisa no período (ou com placa ativa nele)
  const comMovimento = caminhoes.filter((c) => (c.km.km !== null || c.custo !== 0 || c.cargas > 0 || placaAtivaNoPeriodo(d, c.veiculo.id, periodo)
    || c.emAberto?.valor > 0) && (!veiculoId || c.veiculo.id === veiculoId));
  const soma = (campo) => reais(meses.reduce((s, m) => s + m[campo], 0));
  return {
    de: periodo.de,
    ate: periodo.ate,
    rateio,
    despesas,
    veiculoId,
    caminhoes: comMovimento.sort((a, b) => String(a.veiculo.placa ?? '').localeCompare(String(b.veiculo.placa ?? ''))),
    gerais: { total: soma('gerais'), semRateio: soma('semRateio'), itens: itensEmReais(meses.flatMap((m) => m.itensFora)) },
    impostos: soma('impostos'),
    itensImpostos: itensEmReais(meses.flatMap((m) => m.itensImpostos)),
    // O que ficou para pagar no fim do período (só "pelo que foi pago"): o
    // dos caminhões está em cada um; aqui, o das despesas sem caminhão.
    emAbertoSemCaminhao: aberto ? doAberto(aberto.semCaminhao) : null,
  };
}
